import { useEffect, useRef, type ReactNode } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { usePageTracking } from '@/hooks/usePageTracking';
import { initAnalytics, restoreConsent, setUserId, setUserProperties } from '@/services/analytics';

const GA_MEASUREMENT_ID = import.meta.env.VITE_GA_MEASUREMENT_ID as string | undefined;

interface AnalyticsProviderProps {
  readonly children: ReactNode;
}

/**
 * Wrap inside <BrowserRouter> and <AuthProvider> so it has access to
 * both route location and auth state.
 *
 * Responsibilities:
 *   1. Initialize GA4 once on mount
 *   2. Track page views on every route change
 *   3. Sync user properties when auth state changes
 */
export const AnalyticsProvider = ({ children }: AnalyticsProviderProps) => {
  const initRef = useRef(false);
  const { user, isAdmin } = useAuth();

  // 1. Init GA4 (once)
  useEffect(() => {
    if (initRef.current) return;
    if (GA_MEASUREMENT_ID) {
      initAnalytics(GA_MEASUREMENT_ID);
      restoreConsent(); // Apply persisted consent choice (if any) immediately
      initRef.current = true;
    }
  }, []);

  // 2. SPA page view tracking
  usePageTracking();

  // 3. Sync auth → GA4 user properties (non-PII only)
  useEffect(() => {
    if (!initRef.current) return;

    if (user) {
      setUserId(user.userId); // opaque Cognito ID -- not email
      setUserProperties({
        user_type: isAdmin ? 'admin' : 'authenticated',
      });
    } else {
      setUserId(null);
      setUserProperties({ user_type: 'anonymous' });
    }
  }, [user, isAdmin]);

  return <>{children}</>;
};
